/**
 * Six simple report pages built on top of GenericReportPage —
 * each is a thin wrapper that calls the matching API method and
 * adapts the response shape into the GenericReport contract.
 */
import { api } from '@/api/client';
import type {
  BusinessYogaResponse,
  LegalYogaResponse,
  ManglikResponse,
  ProgenyYogaResponse,
  WealthYogaResponse,
} from '@/api/types';

import { GenericReportPage } from './GenericReportPage';
import type { GenericReport } from './GenericReportPage';

// ─── Wealth ───────────────────────────────────────────────────────────────

export function WealthPage({ onBack }: { onBack: () => void }) {
  return (
    <GenericReportPage<WealthYogaResponse>
      title_hi="धन योग" title_en="Wealth Yoga"
      subtitle_hi="आय-स्रोत, ऋण-जोखिम एवं सम्पत्ति-समय का विश्लेषण।"
      subtitle_en="Earning sources, debt risk, and property-timing analysis."
      emoji="💰"
      fetchFn={(req) => api.wealthYoga(req)}
      onBack={onBack}
      toReport={(d): GenericReport => ({
        score: d.wealth_score,
        groups: [
          {
            title_hi: 'आय-प्रवृत्ति', title_en: 'Earning lean',
            text_hi: d.business_vs_job_hi, text_en: d.business_vs_job_en,
          },
          {
            title_hi: 'ऋण-जोखिम', title_en: 'Debt risk',
            text_hi: d.debt_risk_hi, text_en: d.debt_risk_en,
          },
          {
            title_hi: 'सम्पत्ति-समय', title_en: 'Property timing',
            text_hi: d.real_estate_window_hi, text_en: d.real_estate_window_en,
          },
        ],
        sections: d.sections,
        summary_hi: d.summary_hi, summary_en: d.summary_en,
        rules_version: d.rules_version,
        takeaways_hi: d.takeaways_hi, takeaways_en: d.takeaways_en,
        methods: d.methods, inner_practice: d.inner_practice,
        disclaimer_hi: d.disclaimer_hi, disclaimer_en: d.disclaimer_en,
      })}
    />
  );
}

// ─── Manglik ──────────────────────────────────────────────────────────────

export function ManglikPage({ onBack }: { onBack: () => void }) {
  return (
    <GenericReportPage<ManglikResponse>
      title_hi="मंगल दोष विश्लेषण" title_en="Manglik Analysis"
      subtitle_hi="गंभीरता, न्यूनीकरण-कारक एवं उपाय।"
      subtitle_en="Severity, mitigation factors, and remedies."
      emoji="🔴"
      fetchFn={(req) => api.manglik(req)}
      onBack={onBack}
      toReport={(d): GenericReport => {
        const groups: GenericReport['groups'] = [
          {
            title_hi: d.is_manglik
              ? `मंगल-दोष उपस्थित — ${d.severity} स्तर`
              : 'मंगल-दोष अनुपस्थित',
            title_en: d.is_manglik
              ? `Manglik present — ${d.severity} level`
              : 'No Manglik dosha',
            text_hi: d.house_text_hi, text_en: d.house_text_en,
          },
        ];
        if (d.mitigation_factors_hi.length > 0) {
          groups.push({
            title_hi: 'न्यूनीकरण-कारक', title_en: 'Mitigation factors',
            bullets: d.mitigation_factors_hi.map((hi, i) => ({
              hi, en: d.mitigation_factors_en[i] ?? '',
            })),
          });
        }
        if (d.remedies_hi.length > 0) {
          groups.push({
            title_hi: 'उपाय', title_en: 'Remedies',
            bullets: d.remedies_hi.map((hi, i) => ({
              hi, en: d.remedies_en[i] ?? '',
            })),
          });
        }
        return {
          groups,
          summary_hi: d.summary_hi, summary_en: d.summary_en,
          rules_version: d.rules_version,
          takeaways_hi: d.takeaways_hi, takeaways_en: d.takeaways_en,
          methods: d.methods, inner_practice: d.inner_practice,
          disclaimer_hi: d.disclaimer_hi, disclaimer_en: d.disclaimer_en,
        };
      }}
    />
  );
}

// Visa Yoga moved to its own standalone page (VisaYogaPage.tsx) — Phase 6.2.
// The v2 engine has a two-layer report shape that doesn't fit GenericReportPage.

// ─── Business ─────────────────────────────────────────────────────────────

export function BusinessYogaPage({ onBack }: { onBack: () => void }) {
  return (
    <GenericReportPage<BusinessYogaResponse>
      title_hi="व्यवसाय योग्यता" title_en="Business Suitability"
      subtitle_hi="नौकरी बनाम स्व-व्यवसाय; अनुशंसित क्षेत्र।"
      subtitle_en="Job vs business; recommended sectors."
      emoji="🚀"
      fetchFn={(req) => api.businessYoga(req)}
      onBack={onBack}
      toReport={(d): GenericReport => ({
        score: d.business_suitability_score,
        groups: [
          {
            title_hi: 'नौकरी बनाम व्यवसाय', title_en: 'Job vs business',
            text_hi: d.job_vs_business_hi, text_en: d.job_vs_business_en,
          },
          {
            title_hi: 'अनुशंसित क्षेत्र', title_en: 'Recommended sectors',
            bullets: d.recommended_sectors_hi.map((hi, i) => ({
              hi, en: d.recommended_sectors_en[i] ?? '',
            })),
          },
          {
            title_hi: 'लॉन्च-दशक', title_en: 'Launch decade',
            text_hi: d.best_launch_decade_hi, text_en: d.best_launch_decade_en,
          },
        ],
        sections: d.sections,
        summary_hi: d.summary_hi, summary_en: d.summary_en,
        rules_version: d.rules_version,
        takeaways_hi: d.takeaways_hi, takeaways_en: d.takeaways_en,
        methods: d.methods, inner_practice: d.inner_practice,
        disclaimer_hi: d.disclaimer_hi, disclaimer_en: d.disclaimer_en,
      })}
    />
  );
}

// ─── Progeny ──────────────────────────────────────────────────────────────

export function ProgenyYogaPage({ onBack }: { onBack: () => void }) {
  return (
    <GenericReportPage<ProgenyYogaResponse>
      title_hi="संतान योग" title_en="Progeny Yoga"
      subtitle_hi="पंचम भाव विश्लेषण; उपयुक्त समय। (लिंग-भविष्यवाणी सिद्धांततः नहीं।)"
      subtitle_en="5th house analysis; suitable timing. (No gender prediction by policy.)"
      emoji="👶"
      fetchFn={(req) => api.progenyYoga(req)}
      onBack={onBack}
      toReport={(d): GenericReport => {
        const groups: GenericReport['groups'] = [
          {
            title_hi: 'पंचम भाव स्थिति', title_en: '5th house status',
            text_hi: d.fifth_house_status_hi, text_en: d.fifth_house_status_en,
          },
          {
            title_hi: 'सर्वोत्तम समय', title_en: 'Best window',
            text_hi: d.best_decade_hi, text_en: d.best_decade_en,
          },
        ];
        if (d.notes_hi.length > 0) {
          groups.push({
            title_hi: 'टिप्पणियाँ', title_en: 'Notes',
            bullets: d.notes_hi.map((hi, i) => ({ hi, en: d.notes_en[i] ?? '' })),
          });
        }
        return {
          score: d.yoga_strength_score,
          groups,
          sections: d.sections,
          summary_hi: d.summary_hi, summary_en: d.summary_en,
          rules_version: d.rules_version,
          takeaways_hi: d.takeaways_hi, takeaways_en: d.takeaways_en,
          methods: d.methods, inner_practice: d.inner_practice,
          disclaimer_hi: d.disclaimer_hi, disclaimer_en: d.disclaimer_en,
        };
      }}
    />
  );
}

// ─── Legal ────────────────────────────────────────────────────────────────

export function LegalYogaPage({ onBack }: { onBack: () => void }) {
  return (
    <GenericReportPage<LegalYogaResponse>
      title_hi="न्यायिक योग" title_en="Legal Yoga"
      subtitle_hi="विवाद-दृष्टिकोण एवं अनुकूल समय।"
      subtitle_en="Litigation outlook and favourable window."
      emoji="⚖️"
      fetchFn={(req) => api.legalYoga(req)}
      onBack={onBack}
      notice={{
        hi: 'यह रिपोर्ट क़ानूनी सलाह का विकल्प नहीं है — योग्य वकील से अवश्य परामर्श लें।',
        en: 'This report is NOT a substitute for legal counsel — please consult a qualified lawyer.',
      }}
      toReport={(d): GenericReport => ({
        groups: [
          {
            title_hi: 'विवाद-दृष्टिकोण', title_en: 'Litigation outlook',
            text_hi: d.litigation_outlook_hi, text_en: d.litigation_outlook_en,
          },
          {
            title_hi: 'अनुकूल समय', title_en: 'Favourable window',
            text_hi: d.favourable_window_hi, text_en: d.favourable_window_en,
          },
          {
            title_hi: 'परामर्श', title_en: 'Advisory',
            bullets: d.advisory_hi.map((hi, i) => ({
              hi, en: d.advisory_en[i] ?? '',
            })),
          },
        ],
        sections: d.sections,
        summary_hi: d.summary_hi, summary_en: d.summary_en,
        rules_version: d.rules_version,
        takeaways_hi: d.takeaways_hi, takeaways_en: d.takeaways_en,
        methods: d.methods, inner_practice: d.inner_practice,
        disclaimer_hi: d.disclaimer_hi, disclaimer_en: d.disclaimer_en,
      })}
    />
  );
}
